import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CardHightlight, TableExpenses } from "../../components";
import moment from "moment";
import Dialog from "@rio-cloud/rio-uikit/Dialog";
import DialogCreateType from "../../components/Dialog/Create";
import DialogObservation from "../../components/Dialog/Observation";
import {
  createBranch,
  createCategory,
  createSegments,
  createSubCategories,
  createSuppliers,
  createTransaction,
  deleteFile,
  deleteTransaction,
  editTransaction,
  exportTransactions,
  getDetailTransaction,
  getFile,
  getTableData,
} from "../../services/form";
import { Button, Notification, Spinner } from "@rio-cloud/rio-uikit";
import { FormState, useExpensesForm } from "./useExpensesForm";
import { Vehicle } from "../../components/Table/Expenses/Table";
import { formatFileName, getRegionalErrorMessage, getTransactionSaveErrorMessage, parseCurrencyBRL } from "../../utils";
import { TransactionsResponseData } from "../../types";
import DialogCreateCategory from "../../components/Dialog/CreateCategory";
import { allowedTypes, MAX_SIZE } from "../../utils/file";
import dashboardService from "../../services/dashboard";
import { formatDateRange } from "../../utils/dateRange";

import {
  useGetStoredSelectedRegional,
  useGetStoredSelectedClients,
  useGetStoredSelectedContracts,
  useGetStoredSelectedVehicles,
} from "@store/volksCustomAssetTree/volksCustomAssetTreeSlice";
import { parseAssetNodeId } from "../../components/CustomAssetTree/useCustomAssetTree";

import { useAppDispatch } from "../../store";
import { setEndDate, setStartDate, useGetStoredEndDate, useGetStoredStartDate } from "@store/date/dateSlice";
import SmallMonthlyNavigation from "../../components/SmallMonthlyNavigation";
import DeleteSelectedButton from "../../components/Table/DeleteSelectedButton";
import DialogDeleteBatch from "../../components/Dialog/DeleteBatch";
import { useTransactionsBatchDelete } from "../../hooks/useTransactionsBatchDelete";

interface ITableData {
  driverIds: string[];
  id: string;
  transactionDate: string;
  amount: number;
  branchName: string;
  segmentBussinessName: string;
  clientName: string;
  vehicleName: string;
  driverName: string;
  categoryName: string;
  subcategoryName: string;
  taxPercentage: string;
  contract: string;
  requestNumber: string;
  regionalName: string;
  amountType: string;
}

export type NameInputChange =
  | "date"
  | "value"
  | "branch"
  | "segment"
  | "supplier"
  | "trip"
  | "vehicle"
  | "driver"
  | "category"
  | "subcategory"
  | "taxes"
  | "observation";

interface Option {
  [x: string]: { placeholder: string; title: string };
}

interface fileAdjustmentsProps {
  rowNumber?: number;
  columnName?: string;
  errorMessage?: string;
}

const defaultOption = {
  open: false,
  type: "",
  title: "",
  value: "",
  placeholder: "",
};

interface AssetTreeFilters {
  regionalIds: string[];
  clientIds: string[];
  contracts: string[];
  vehicleIds: string[];
}

const ExpensesScreen = () => {
  const dispatch = useAppDispatch();
  const PAGE_SIZE = 100;
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState(false);

  const startDate = useGetStoredStartDate();
  const endDate = useGetStoredEndDate();

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Auto-open modal if add=expense query param is present
  useEffect(() => {
    if (searchParams.get("add") === "expense") {
      setOpenModal(true);
      searchParams.delete("add");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [totalExpenseAmount, setTotalExpenseAmount] = useState(0);
  const [editingExpenseId, setEditingExpenseId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteConfirmFileId, setDeleteConfirmFileId] = useState<string | null>(null);
  const [observation, setObservation] = useState({
    open: false,
    value: "",
  });
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{
    status: string;
    processedRows: number;
    totalRows: number;
  } | null>(null);

  const [dialogOption, setDialogOption] = useState(defaultOption);
  const [tableData, setTableData] = useState<Vehicle[]>([]);
  const [fileName, setFileName] = useState("");
  const [errorFile, setErrorFile] = useState("");
  const [fileAdjustments, setFileAdjustments] = useState<fileAdjustmentsProps[]>([]);
  const [loadFile, setLoadFile] = useState(false);
  const [loadingTable, setIsLoadingTable] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [totalExpensesRecords, setTotalExpensesRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  const batchDelete = useTransactionsBatchDelete({
    resetOn: tableData,
    onDeleted: () => fetchInitial(),
    errorMessage: "Não foi possível excluir as despesas selecionadas.",
  });

  const selectedRegional = useGetStoredSelectedRegional();
  const selectedClients = useGetStoredSelectedClients();
  const selectedContracts = useGetStoredSelectedContracts();
  const selectedVehicles = useGetStoredSelectedVehicles();

  const requestFilters = () => {
    const filters: AssetTreeFilters = {
      regionalIds: [],
      clientIds: [],
      contracts: [],
      vehicleIds: [],
    };

    if (selectedRegional && selectedRegional.length > 0) {
      filters.regionalIds = selectedRegional.map((item) => parseAssetNodeId(item).regionalId).filter(Boolean);
    }

    const selectedRegionalIds = new Set(filters.regionalIds);

    if (selectedClients && selectedClients.length > 0) {
      filters.clientIds = selectedClients
        .map((item) => parseAssetNodeId(item))
        .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
        .map((parsed) => parsed.clientId)
        .filter(Boolean);
    }

    if (selectedContracts && selectedContracts.length > 0) {
      filters.contracts = selectedContracts
        .map((item) => parseAssetNodeId(item))
        .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
        .map((parsed) => parsed.contract)
        .filter(Boolean);
    }

    if (selectedVehicles && selectedVehicles.length > 0) {
      filters.vehicleIds = selectedVehicles
        .map((item) => parseAssetNodeId(item))
        .filter((parsed) => !selectedRegionalIds.has(parsed.regionalId))
        .map((parsed) => parsed.vehicleId)
        .filter(Boolean);
    }

    return filters;
  };

  const {
    branchOptions,
    segmentsOptions,
    supplierOptions,
    driversOptions,
    categoriesOptions,
    vehiclesOptions,
    subCategoriesOptions,
    form,
    initialDefaultForm,
    setForm,
    fetchBranchOptions,
    fetchSegments,
    fetchSupplier,
    fetchCategories,
    fetchSubCategories,
  } = useExpensesForm();

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const validateFile = (file: File | null): string => {
    if (!file) return "";

    if (file.size > MAX_SIZE) {
      return "O arquivo excede o tamanho máximo de 10 MB.";
    }

    if (!allowedTypes.includes(file.type)) {
      return "Formato não permitido. Use XLS ou XLSX.";
    }

    return "";
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    const name = formatFileName(file?.name);
    const validation = validateFile(file);

    if (!!validation) {
      setErrorFile(validation);
      setFileName("");
      return;
    }

    if (file) {
      setLoadFile(true);
      setErrorFile("");
      setFileName(name);
      setUploadProgress(null);

      const renamedFile = new File([file], name, { type: file.type, lastModified: file.lastModified });

      setFile(renamedFile);

      // Callback para atualizar progresso do upload
      const onProgress = (status: string, processedRows: number, totalRows: number) => {
        setUploadProgress({ status, processedRows, totalRows });
      };

      const response = await dashboardService.uploadExpenseFile(renamedFile, onProgress);
      setLoadFile(false);
      setUploadProgress(null);

      if (!response) {
        Notification.error("Erro ao anexar arquivo.");
        return;
      }

      const uploadStatus = response?.data?.upload?.status;

      if (uploadStatus === "FAILED") {
        Notification.error("Falha no processamento do arquivo.");
        if (response?.data?.errors?.length) {
          setFileAdjustments(response.data.errors);
        }
        return;
      }

      if (uploadStatus === "PARTIAL" || response?.data?.errors?.length) {
        const successRows = response?.data?.upload?.successRows || 0;
        const errorRows = response?.data?.upload?.errorRows || 0;
        Notification.warning(`Upload parcial: ${successRows} importados, ${errorRows} erros`);
        setFileAdjustments(response.data.errors);
        return;
      }

      const successRows = response?.data?.upload?.successRows || 0;
      Notification.success(`${successRows} transacoes importadas com sucesso.`);
      setOpenModal(false);
      fetchInitial();
    }
  };

  const onCreateNewOption = (name: NameInputChange) => {
    // Operacao/Segmento usa dialog especifico com 2 campos
    // if (name === 'segment') {
    //   setShowOperationDialog(true);
    //   return;
    // }

    const opt: Option = {
      branch: {
        placeholder: "Qual o nome da nova regional?",
        title: "Nova regional",
      },
      supplier: {
        placeholder: "Qual o nome do novo(a) fornecedor?",
        title: "Novo(a) fornecedor",
      },
      category: {
        placeholder: "Qual a nova categoria?",
        title: "Nova categoria",
      },
      subcategory: {
        placeholder: "Qual a nova subcategoria?",
        title: "Nova subcategoria",
      },
      segment: {
        placeholder: "Qual o novo segmento?",
        title: "Novo segmento",
      },
    };

    setDialogOption({
      open: true,
      placeholder: opt[name]?.placeholder,
      title: opt[name]?.title,
      type: name,
      value: "",
    });
  };

  const handleSaveBranch = async (value: string) => {
    try {
      const response = await createBranch({
        name: value,
      });
      fetchBranchOptions();
      setForm({
        ...form,
        branch: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success("Regional criada com sucesso.");
    } catch (error) {
      Notification.error(getRegionalErrorMessage(error, "Não foi possível criar a regional no momento."));
    }
  };

  const handleSaveOperation = async (value: string) => {
    try {
      const response = await createSegments(value);
      fetchSegments();
      setForm({
        ...form,
        segment: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Segmento criado com sucesso.");
    } catch (error) {
      Notification.error("Nao foi possivel criar a operacao no momento.");
    }
  };

  const handleSaveSupplier = async (value: string) => {
    try {
      const response = await createSuppliers({
        name: value,
        type: "FORNECEDOR",
      });
      fetchSupplier();
      setForm({
        ...form,
        supplier: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Fornecedor criado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar o fornecedor no momento.");
    }
  };

  const handleSaveCategory = async (value: string, selectValue?: string) => {
    try {
      const response = await createCategory({
        name: value,
        type: "DESPESA",
        expenseType: selectValue as "DIRETO" | "OPERACIONAL",
      });
      fetchCategories();
      setForm({
        ...form,
        category: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      setOpenModal(true);
      Notification.success("Categoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a categoria no momento.");
    }
  };

  const handleSaveSubCategory = async (value: string) => {
    try {
      const response = await createSubCategories(form.category, { name: value });
      fetchSubCategories();
      setForm({
        ...form,
        subcategory: response.data.id.toString(),
      });
      setDialogOption(defaultOption);
      Notification.success("Subcategoria criada com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível criar a subcategoria no momento.");
    }
  };

  const handleSave = (value: string, selectValue?: string) => {
    if (dialogOption.type === "branch") {
      handleSaveBranch(value);
      return;
    }

    if (dialogOption.type === "supplier") {
      handleSaveSupplier(value);
      return;
    }

    if (dialogOption.type === "category") {
      handleSaveCategory(value, selectValue);
      return;
    }

    if (dialogOption.type === "subcategory") {
      handleSaveSubCategory(value);
      return;
    }
    if (dialogOption.type === "segment") {
      handleSaveOperation(value);
      return;
    }
  };

  const fetchTableData = async (page = 0, append = false) => {
    const setLoading = append ? setIsLoadingMore : setIsLoadingTable;

    setLoading(true);

    const getFilters = requestFilters();

    try {
      const response = await getTableData(getFilters, startDate, endDate, "DESPESA", {
        page,
        size: PAGE_SIZE,
      });

      const normalizedPayload: TransactionsResponseData = response?.data ?? response;

      const content = Array.isArray(normalizedPayload?.content) ? normalizedPayload?.content : [];

      const formattedRows: Vehicle[] = content.map((item: ITableData) => {
        return {
          id: item.id.toString(),
          transactionType: item?.amountType,
          date: moment(item.transactionDate).format("DD/MM/YYYY"),
          amount: item.amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }),
          segment: item.segmentBussinessName,
          regional: item?.regionalName,
          customer: item.clientName,
          contract: item.contract,
          vehicle: item.vehicleName,
          registration: item.requestNumber,
          category: item.categoryName,
          subcategory: item.subcategoryName,
          sortableDate: "",
          sortableAmount: 0,
          branch: "",
          driver: [],
          taxes: "",
        };
      });

      setTableData((prev) => (append ? [...prev, ...formattedRows] : formattedRows));
      setTotalExpensesRecords(normalizedPayload?.totalElements);
      setCurrentPage(page);
    } catch (error) {
      Notification.error("Não foi possível carregar as despesas.");
    } finally {
      setLoading(false);
    }
  };

  const fetchInitial = async () => {
    fetchTableData();
  };

  const handleLoadMore = () => {
    if (isLoadingMore || tableData.length >= totalExpensesRecords) {
      return;
    }

    fetchTableData(currentPage + 1, true);
  };

  useEffect(() => {
    fetchInitial();
  }, [startDate, endDate, selectedRegional, selectedClients, selectedContracts, selectedVehicles]);

  const saveNewExpense = async (data: FormState, callback?: () => void, dialogModal = true) => {
    try {
      await createTransaction(
        {
          amount: parseCurrencyBRL(data.value),
          regionalId: Number(data.branch),
          categoryId: Number(data.category),
          description: data.observation,
          driverId: data.driver.length > 0 ? data.driver : null,
          vehicleId: data.vehicle ? data.vehicle : null,
          segmentBussinessId: Number(data.segment),
          subcategoryId: Number(data.subcategory),
          supplierClientId: Number(data.supplier),
          taxPercentage: 0,
          transactionDate: data.date,
          tripCode: data.trip,
          transactionType: "DESPESA",
        },
        file || undefined
      );

      if (dialogModal) {
        setOpenModal(false);
      }

      Notification.success("Despesa criada com sucesso.");
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(getTransactionSaveErrorMessage(error, "Não foi possível criar a despesa."));
    }
  };

  const handleEditExpense = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      const expense = response.data;

      if (expense?.fileNameAttachmentUrl) {
        setFileName(expense?.fileNameAttachmentUrl);
      }

      setForm({
        date: expense.transactionDate?.split("T")[0] || "",
        value: expense.amount?.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) || "",
        branch: (expense.regionalId ?? expense.branchId)?.toString() || "",
        segment: expense.segmentBussinessId?.toString() || "",
        supplier: expense.supplierClientId?.toString() || "",
        trip: expense?.tripCode,
        vehicle: expense.vehicleId || "",
        driver: expense.driverIds || (expense.driverId ? [expense.driverId] : []),
        category: expense.categoryId?.toString() || "",
        subcategory: expense.subcategoryId?.toString() || "",
        observation: expense.description || "",
      });

      setEditingExpenseId(id);
      setOpenModal(true);
    } catch (error) {
      Notification.error("Não foi possível carregar os dados da despesa.");
    }
  };

  const handleUpdateExpense = async (data: FormState, callback?: () => void) => {
    if (!editingExpenseId) return;

    try {
      await editTransaction(
        editingExpenseId,
        {
          amount: parseCurrencyBRL(data.value),
          regionalId: Number(data.branch),
          categoryId: Number(data.category),
          description: data.observation,
          driverId: data.driver.length > 0 ? data.driver : null,
          vehicleId: data.vehicle ? data.vehicle : null,
          segmentBussinessId: Number(data.segment),
          subcategoryId: Number(data.subcategory),
          supplierClientId: Number(data.supplier),
          taxPercentage: 0,
          transactionDate: data.date,
          transactionType: "DESPESA",
        },
        file || undefined
      );

      setOpenModal(false);
      setEditingExpenseId(null);
      Notification.success("Despesa atualizada com sucesso.");
      callback?.();
      fetchInitial();
    } catch (error) {
      Notification.error(getTransactionSaveErrorMessage(error, "Não foi possível atualizar a despesa."));
    }
  };

  const handleDeleteExpense = async (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteFileExpense = async (id: string) => {
    setDeleteConfirmFileId(id);
  };

  const confirmDeleteFileExpense = async () => {
    if (!deleteConfirmFileId) return;

    try {
      await deleteFile(deleteConfirmFileId);
      setDeleteConfirmFileId(null);
      Notification.success("Arquivo excluído com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir o arquivo.");
    }
  };

  const confirmDeleteExpense = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteTransaction(deleteConfirmId);
      setDeleteConfirmId(null);
      Notification.success("Despesa excluída com sucesso.");
      fetchInitial();
    } catch (error) {
      Notification.error("Não foi possível excluir a despesa.");
    }
  };

  const getDownloadFile = async (transactionId: string) => {
    try {
      const response = await getFile(transactionId);

      window.open(response.data.downloadUrl, "_blank");
      Notification.success("Comprovante baixado com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível baixar o comprovante.");
    }
  };

  const handleDefaultForm = () => {
    setForm(initialDefaultForm);
    setEditingExpenseId(null);
  };

  const handleObservationDefault = () => {
    setObservation({
      open: false,
      value: "",
    });
  };

  const handleObservation = async (id: string) => {
    try {
      const response = await getDetailTransaction(id);
      setObservation({
        open: true,
        value: response?.data?.description,
      });
    } catch (error) {
      Notification.error("Não foi possível obter os detalhes da despesa.");
      handleObservationDefault();
    }
  };

  const handleSaveAndCreateNew = () => {
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      return;
    }
    saveNewExpense(form, handleDefaultForm, false);
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!form.date) newErrors.date = "Informe a data";
    if (!form.value) newErrors.value = "Informe o valor";
    if (!form.branch) newErrors.branch = "Selecione a regional";
    if (!form.category) newErrors.category = "Selecione a categoria";
    return newErrors;
  };

  const fileClear = () => {
    setFileName("");
    setFile(null);
    setFileAdjustments([]);
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = "/templates/modelo_importacao_despesas_volkstotal.xlsx";
    link.download = "modelo_importacao_despesas_volkstotal.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getModalFileUpload = () => {
    const progressPercentage =
      uploadProgress && uploadProgress.totalRows > 0
        ? Math.round((uploadProgress.processedRows / uploadProgress.totalRows) * 100)
        : 0;

    return (
      <div className="form-group display-flex justify-content-center flex-column align-items-center padding-20">
        {loadFile && !uploadProgress ? <Spinner /> : null}

        {/* Progress bar durante upload assincrono */}
        {uploadProgress && (
          <div className="width-100pct margin-bottom-20">
            <div className="display-flex justify-content-between margin-bottom-5">
              <span className="text-color-dark">Processando arquivo...</span>
              <span className="text-color-primary text-bold">{progressPercentage}%</span>
            </div>
            <div className="progress" style={{ height: "20px" }}>
              <div
                className="progress-bar progress-bar-primary"
                role="progressbar"
                style={{ width: `${progressPercentage}%` }}
                aria-valuenow={progressPercentage}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
            <div className="text-center margin-top-5 text-color-gray">
              {uploadProgress.processedRows.toLocaleString("pt-BR")} de{" "}
              {uploadProgress.totalRows.toLocaleString("pt-BR")} linhas
            </div>
          </div>
        )}

        {!loadFile ? (
          <div
            className="display-flex align-items-center justify-content-center flex-wrap margin-bottom-10"
            style={{ gap: "10px" }}
          >
            <Button onClick={handleButtonClick} iconName="rioglyph rioglyph-upload" className="rounded-small">
              Anexar arquivo despesa
            </Button>
            <Button onClick={handleDownloadTemplate} iconName="rioglyph rioglyph-download" className="rounded-small">
              Baixar Modelo Excel
            </Button>
          </div>
        ) : null}
        {fileName && !uploadProgress && (
          <Button bsStyle={Button.MUTED} iconName="rioglyph rioglyph-remove-sign" iconRight onClick={fileClear}>
            {fileName}
          </Button>
        )}
        <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
        {errorFile && <p className="text-color-danger margin-0 margin-top-5">{errorFile}</p>}

        {fileAdjustments.length > 0 && (
          <div className="margin-top-20 width-100pct max-height-200px overflow-auto">
            <p>Erros encontrados no arquivo:</p>
            <table className="table table-striped table-bordered">
              <thead>
                <tr>
                  <th>Coluna</th>
                  <th>Linha</th>
                  <th>Mensagem de Erro</th>
                </tr>
              </thead>
              <tbody>
                {fileAdjustments.map((adjustment, index) => (
                  <tr key={index}>
                    <td>{adjustment.columnName || "-"}</td>
                    <td>{adjustment.rowNumber === 0 ? "Geral / Arquivo" : adjustment.rowNumber}</td>
                    <td>{adjustment.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  const handleDownload = async () => {
    try {
      await exportTransactions(startDate, endDate, "DESPESA");
      Notification.success("Despesas exportadas com sucesso.");
    } catch (error) {
      Notification.error("Não foi possível exportar as despesas.");
    }
  };

  return (
    <div className="padding-20 display-flex flex-column gap-20 min-height-100pct">
      <div className="display-flex width-100pct gap-50">
        <div className="width-50pct">
          <SmallMonthlyNavigation endDate={endDate} startDate={startDate} />
        </div>
        <div className="width-50pct">
          {/* <CardHightlight
            icon="rioglyph rioglyph-arrow-down text-size-h1 text-color-danger"
            label='Valor total das despesas'
            value={totalExpenseAmount.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            direction="RIGHT"
          /> */}
        </div>
      </div>
      <div className="flex-1-1 height-100pct">
        <TableExpenses
          data={tableData}
          totalData={totalExpensesRecords}
          startDate={startDate}
          endDate={endDate}
          handleStartDate={(date) => dispatch(setStartDate(date))}
          handleEndDate={(date) => dispatch(setEndDate(date))}
          viewType="TABLE"
          onLoadMore={handleLoadMore}
          handleRefresh={() => fetchInitial()}
          handleDownload={handleDownload}
          openModal={() => {
            setFileName("");
            setOpenModal(true);
          }}
          onEdit={handleEditExpense}
          onDelete={handleDeleteExpense}
          onDeleteFile={handleDeleteFileExpense}
          onGetFile={getDownloadFile}
          onObservation={handleObservation}
          isLoading={loadingTable}
          isLoadingMore={isLoadingMore}
          selectable
          selectedIds={batchDelete.selectedIds}
          onToggleSelectRow={batchDelete.toggleRow}
          onToggleSelectAll={batchDelete.toggleAll}
          onClearSelection={batchDelete.clearSelection}
          extraToolbarActions={
            <DeleteSelectedButton
              selectedCount={batchDelete.selectedIds.length}
              disabled={batchDelete.isDeleting}
              onClick={batchDelete.openConfirm}
            />
          }
        />

        <Dialog
          show={openModal && !dialogOption.open}
          title={
            <span className="text-medium text-size-16">{editingExpenseId ? "Editar despesa" : "Nova despesa"}</span>
          }
          body={getModalFileUpload()}
          onClose={() => {
            setOpenModal(false);
            handleDefaultForm();
          }}
          className="exampleDialog"
          showCloseButton
          bsSize="md"
        />

        <DialogCreateType
          title={dialogOption.title}
          show={dialogOption.type !== "category" && dialogOption.open}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
          }}
          onSave={handleSave}
        />

        <DialogCreateCategory
          title={dialogOption.title}
          show={dialogOption.type === "category" && dialogOption.open}
          name={dialogOption.type}
          placeholder={dialogOption.placeholder}
          onClose={() => {
            setOpenModal(true);
            setDialogOption(defaultOption);
          }}
          onSave={handleSave}
        />

        {/* <DialogCreateOperation
          show={showOperationDialog}
          onClose={() => { setShowOperationDialog(false); setOpenModal(true); }}
          onSave={handleSaveOperation}
        /> */}

        <DialogDeleteBatch
          show={batchDelete.showConfirm}
          title="Excluir despesas selecionadas"
          count={batchDelete.selectedIds.length}
          entityLabel="despesa(s) selecionada(s)"
          isLoading={batchDelete.isDeleting}
          onConfirm={batchDelete.confirmDelete}
          onClose={batchDelete.closeConfirm}
        />

        <Dialog
          show={deleteConfirmId !== null}
          title={<span className="text-medium text-size-16">Excluir despesa</span>}
          body={
            <div className="padding-20">
              <p>Tem certeza que deseja excluir esta despesa?</p>
              <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
            </div>
          }
          footer={
            <div className="display-flex gap-10 justify-content-end">
              <button className="btn btn-default" onClick={() => setDeleteConfirmId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteExpense}>
                Excluir
              </button>
            </div>
          }
          onClose={() => setDeleteConfirmId(null)}
          className="exampleDialog"
          showCloseButton
          bsSize="sm"
        />

        <Dialog
          show={deleteConfirmFileId !== null}
          title={<span className="text-medium text-size-16">Excluir comprovante</span>}
          body={
            <div className="padding-20">
              <p>Tem certeza que deseja excluir este comprovante?</p>
              <p className="text-color-danger">Esta ação não pode ser desfeita.</p>
            </div>
          }
          footer={
            <div className="display-flex gap-10 justify-content-end">
              <button className="btn btn-default" onClick={() => setDeleteConfirmFileId(null)}>
                Cancelar
              </button>
              <button className="btn btn-danger" onClick={confirmDeleteFileExpense}>
                Excluir
              </button>
            </div>
          }
          onClose={() => setDeleteConfirmFileId(null)}
          className="exampleDialog"
          showCloseButton
          bsSize="sm"
        />

        <DialogObservation
          onClose={handleObservationDefault}
          show={observation.open}
          title="Observações"
          value={observation.value}
        />
      </div>
    </div>
  );
};

export default ExpensesScreen;
